// src/components/common/vibe/CelebrationPostModal.js
import React, { useState, useRef } from 'react';
import { X, Send, Cake, Award, User } from 'lucide-react';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GifPicker';
import vibeService from '../../../services/vibe.service';

const CelebrationPostModal = ({ isOpen, onClose, employee, celebrationType, theme, onPostCreated }) => {
    const [content, setContent] = useState('');
    const [selectedGif, setSelectedGif] = useState(null);
    const [loading, setLoading] = useState(false);
    const textareaRef = useRef(null);

    // Generate default message based on celebration type
    const getDefaultMessage = () => {
        if (!employee) return '';

        const firstName = employee.first_name;
        const pronoun = getPronoun(employee.gender);

        if (celebrationType === 'birthday') {
            return `Happy Birthday ${firstName}! 🎂🎉 Wishing you a wonderful day filled with joy and happiness! `;
        } else if (celebrationType === 'work_anniversary') {
            const years = employee.years_of_service;
            const ordinal = getOrdinal(years);
            return `Congratulations ${firstName} on your ${ordinal} work anniversary! 🏆🎊 Thank you for ${pronoun} dedication and hard work! `;
        }
        return '';
    };

    // Initialize content with default message when modal opens
    React.useEffect(() => {
        if (isOpen && employee) {
            setContent(getDefaultMessage());
        }
    }, [isOpen, employee]);

    const getPronoun = (gender) => {
        if (!gender) return 'their';
        const genderLower = gender.toLowerCase();
        if (genderLower === 'male') return 'his';
        if (genderLower === 'female') return 'her';
        return 'their';
    };

    const getOrdinal = (years) => {
        if (years === 1) return '1st';
        if (years === 2) return '2nd';
        if (years === 3) return '3rd';
        return `${years}th`;
    };

    const handleEmojiSelect = (emoji) => {
        const cursorPosition = textareaRef.current.selectionStart || content.length;
        const textBeforeCursor = content.substring(0, cursorPosition);
        const textAfterCursor = content.substring(cursorPosition);
        const newText = textBeforeCursor + emoji + textAfterCursor;
        setContent(newText);
        textareaRef.current.focus();
    };

    const handleGifSelect = (gif) => {
        setSelectedGif(gif);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!content.trim()) return;

        setLoading(true);
        try {
            // Create celebration marker in content for easy detection
            const celebrationPrefix = celebrationType === 'birthday'
                ? '🎂  '
            : '🏆  ';

            const postData = {
                content: celebrationPrefix + content.trim(),
                post_type: 'celebration',
                visibility: 'company',
                mentioned_employees: [employee.id]
            };

            // Add GIF if selected
            if (selectedGif) {
                postData.gif_url = selectedGif.url;
                postData.gif_title = selectedGif.title;
            }

            console.log('🎉 Creating celebration post:', postData);

            await vibeService.createPost(postData);

            // Notify parent to refresh feed and show confetti there
            if (onPostCreated) {
                await onPostCreated();
            }

            // Reset and close immediately - confetti will show in the feed
            setContent('');
            setSelectedGif(null);
            onClose();
        } catch (error) {
            console.error('Error creating celebration post:', error);
            alert('Failed to create celebration post. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen || !employee) return null;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                {/* Modal */}
                <div
                    className={`rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto ${
                        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
                    }`}
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className={`p-6 border-b ${
                        theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                    }`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                {celebrationType === 'birthday' ? (
                                    <Cake className="w-6 h-6 text-pink-500" />
                                ) : (
                                    <Award className="w-6 h-6 text-amber-500" />
                                )}
                                <h2 className={`text-xl font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    {celebrationType === 'birthday' ? 'Birthday Celebration' : 'Work Anniversary Celebration'}
                                </h2>
                            </div>
                            <button
                                onClick={onClose}
                                className={`p-2 rounded-lg transition-colors ${
                                    theme === 'dark'
                                        ? 'hover:bg-gray-700 text-gray-400'
                                        : 'hover:bg-gray-100 text-gray-600'
                                }`}
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </div>

                    {/* Employee Info */}
                    <div className={`p-6 border-b ${
                        theme === 'dark' ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'
                    }`}>
                        <div className="flex items-center gap-4">
                            {employee.profile_picture_url ? (
                                <img
                                    src={employee.profile_picture_url}
                                    alt={`${employee.first_name} ${employee.last_name}`}
                                    className="w-16 h-16 rounded-full object-cover"
                                />
                            ) : (
                                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                                    theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'
                                }`}>
                                    <User className="w-8 h-8 text-gray-500" />
                                </div>
                            )}
                            <div>
                                <h3 className={`text-lg font-semibold ${
                                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                                }`}>
                                    {employee.first_name} {employee.last_name}
                                </h3>
                                {employee.designation && (
                                    <p className={`text-sm ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        {employee.designation}
                                    </p>
                                )}
                                {celebrationType === 'work_anniversary' && employee.years_of_service && (
                                    <p className={`text-sm mt-1 font-medium ${
                                        theme === 'dark' ? 'text-amber-400' : 'text-amber-600'
                                    }`}>
                                        🏆 {employee.years_of_service} {employee.years_of_service === 1 ? 'Year' : 'Years'} of Service
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="p-6">
                        <div className="space-y-4">
                            {/* Message Textarea */}
                            <div>
                                <label className={`block text-sm font-medium mb-2 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                                }`}>
                                    Your Wishes
                                </label>
                                <textarea
                                    ref={textareaRef}
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Share your wishes..."
                                    rows={5}
                                    className={`w-full px-4 py-3 rounded-lg border resize-none ${
                                        theme === 'dark'
                                            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                            : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-500'
                                    } focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                                />
                            </div>

                            {/* GIF Preview */}
                            {selectedGif && (
                                <div className={`p-3 rounded-lg border ${
                                    theme === 'dark' ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
                                }`}>
                                    <div className="flex items-start gap-3">
                                        <img
                                            src={selectedGif.url}
                                            alt={selectedGif.title}
                                            className="w-32 h-32 rounded object-cover"
                                        />
                                        <div className="flex-1">
                                            <p className={`text-sm font-medium mb-2 ${
                                                theme === 'dark' ? 'text-white' : 'text-gray-900'
                                            }`}>
                                                {selectedGif.title || 'Selected GIF'}
                                            </p>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedGif(null)}
                                                className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                                            >
                                                <X className="w-4 h-4" />
                                                Remove GIF
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Bar */}
                            <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                                <div className="flex items-center gap-2">
                                    {/* Emoji Picker */}
                                    <EmojiPicker
                                        onEmojiSelect={handleEmojiSelect}
                                        theme={theme}
                                    />

                                    {/* GIF Picker */}
                                    <GifPicker
                                        onGifSelect={handleGifSelect}
                                        theme={theme}
                                    />

                                    <span className={`text-sm ml-2 ${
                                        theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                                    }`}>
                                        Add emoji or GIF to make it special!
                                    </span>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={!content.trim() || loading}
                                    className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-medium transition-colors ${
                                        content.trim() && !loading
                                            ? celebrationType === 'birthday'
                                                ? 'bg-pink-600 hover:bg-pink-700 text-white'
                                                : 'bg-amber-600 hover:bg-amber-700 text-white'
                                            : theme === 'dark'
                                                ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                                                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                    }`}
                                >
                                    {loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                            Posting...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Post Celebration
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </>
    );
};

export default CelebrationPostModal;
